/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send } from "lucide-react";

interface Message {
  type: "bot" | "user";
  text: string;
  timestamp: Date;
  isError?: boolean;
}

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [userInput, setUserInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [userData, setUserData] = useState({
    nome: "",
    telefone: "",
    email: "",
    descricao: "",
    isClient: null as boolean | null,
    status: "",
  });
  const [isTyping, setIsTyping] = useState(false);
  const [showClientOptions, setShowClientOptions] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const openButtonRef = useRef<HTMLButtonElement>(null);

  type ChatFlowItem = {
    question: string;
    field: keyof typeof userData;
    type: "text" | "textarea" | "options";
    validation: (value: string | boolean) => string | null;
    options?: { value: boolean; label: string }[];
  };

  const chatFlow: ChatFlowItem[] = [
    {
      question: "Olá! 👋 Seja bem-vindo ao suporte Tech Glacial. Para iniciarmos, qual é o seu nome?",
      field: "nome",
      type: "text",
      validation: (value: string | boolean) => {
        if (typeof value === "string") {
          return value.trim().length > 2 ? null : "Por favor, digite seu nome completo";
        }
        return null;
      },
    },
    {
      question: "Prazer em conhecê-lo! Agora preciso do seu telefone para contato:",
      field: "telefone",
      type: "text",
      validation: (value: string | boolean) => {
        if (typeof value === "string") {
          if (!value.trim()) return "Por favor, digite seu telefone";
          const phone = value.replace(/\D/g, "");
          if (phone.length < 10) return "Telefone deve ter pelo menos 10 dígitos";
        }
        return null;
      },
    },
    {
      question: "Perfeito! Qual é o seu e-mail?",
      field: "email",
      type: "text",
      validation: (value: string | boolean) => {
        if (typeof value === "string") {
          if (!value.trim()) return "Por favor, digite seu e-mail";
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
            return "Por favor, digite um e-mail válido";
        }
        return null;
      },
    },
    {
      question: "Você já é nosso cliente?",
      field: "isClient",
      type: "options",
      options: [
        { value: true, label: "Sim, sou cliente" },
        { value: false, label: "Não, ainda não sou cliente" },
      ],
      validation: (value: string | boolean) =>
        typeof value === "boolean" ? null : "Por favor, selecione uma opção",
    },
    {
      question: "Ótimo! Para finalizar, descreva como podemos ajudá-lo:",
      field: "descricao",
      type: "textarea",
      validation: (value: string | boolean) => {
        if (typeof value === "string") {
          return value.trim().length > 0 ? null : "Por favor, descreva como podemos ajudar";
        }
        return null;
      },
    },
  ];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Foco automático no input quando chat abre
  useEffect(() => {
    if (isOpen && inputRef.current && !showClientOptions) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, currentStep, showClientOptions]);

  // Fechar com ESC
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
        openButtonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  // Trap focus dentro do chat
  useEffect(() => {
    if (!isOpen) return;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      const focusableElements = chatContainerRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      if (e.shiftKey && document.activeElement === firstElement) {
        lastElement?.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        firstElement?.focus();
        e.preventDefault();
      }
    };

    document.addEventListener("keydown", handleTab);
    return () => document.removeEventListener("keydown", handleTab);
  }, [isOpen, showClientOptions]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setTimeout(() => {
        setIsTyping(true);
        setTimeout(() => {
          setIsTyping(false);
          setMessages([
            {
              type: "bot",
              text: chatFlow[0].question,
              timestamp: new Date(),
            },
          ]);
        }, 1500);
      }, 500);
    }
  }, [isOpen, chatFlow, messages.length]);

  const formatPhoneNumber = (value: string) => {
    const phone = value.replace(/\D/g, "");
    if (phone.length <= 11) {
      return phone.replace(/(\d{2})(\d{0,5})(\d{0,4})/, (_match, p1, p2, p3) => {
        if (p3) return `(${p1}) ${p2}-${p3}`;
        if (p2) return `(${p1}) ${p2}`;
        if (p1) return `(${p1}`;
        return phone;
      });
    }
    return value;
  };

  const saveToGoogleSheets = async (data: {
    nome: string;
    telefone: string;
    email: string;
    descricao: string;
    status: string;
  }) => {
    try {
      const sheetData = {
        Nome: data.nome,
        Telefone: data.telefone,
        Email: data.email,
        Descrição: data.descricao,
        "Data/Hora": new Date().toLocaleString("pt-BR"),
        Status: data.status,
        Origem: "Site - Chatbot",
      };

      const GOOGLE_SCRIPT_URL = import.meta.env.VITE_GOOGLE_SCRIPT_URL;

      await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(sheetData),
      });

      console.log("Dados salvos no Google Sheets com sucesso!");
      return true;
    } catch (error) {
      console.error("Erro ao salvar no Google Sheets:", error);
      return false;
    }
  };

  const handleOptionSelect = async (optionValue: boolean) => {
    const currentQuestion = chatFlow[currentStep];
    const selectedOption = currentQuestion.options?.find((opt) => opt.value === optionValue);
    const status = optionValue ? "Cliente" : "Apenas Informações";

    const userMessage: Message = {
      type: "user" as const,
      text: selectedOption ? selectedOption.label : "Opção não selecionada",
      timestamp: new Date(),
    };

    const newMessages: Message[] = [...messages, userMessage];
    setMessages(newMessages);
    setShowClientOptions(false);

    setUserData((prev) => ({
      ...prev,
      isClient: optionValue,
      status: status,
    }));

    if (currentStep < chatFlow.length - 1) {
      setIsTyping(true);

      setTimeout(() => {
        setIsTyping(false);
        const nextQuestion = chatFlow[currentStep + 1];

        const botMessage = {
          type: "bot" as const,
          text: nextQuestion.question,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, botMessage]);
        setCurrentStep(currentStep + 1);

        if (nextQuestion.type === "options") {
          setShowClientOptions(true);
        }
      }, 1500);
    } else {
      await finalizarChat(userData);
    }
  };

  const finalizarChat = async (finalData: typeof userData) => {
    setIsTyping(true);

    setTimeout(async () => {
      setIsTyping(false);

      const finalMessage: Message = {
        type: "bot",
        text: "Obrigado pelas informações! Salvando seus dados e redirecionando você para o WhatsApp... 🚀",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, finalMessage]);

      await saveToGoogleSheets(finalData);

      setTimeout(() => {
        const message = `Olá! Vim através do seu site.

*Nome:* ${finalData.nome}
*Telefone:* ${finalData.telefone}  
*Email:* ${finalData.email}
*Descrição:* ${finalData.descricao}
*Status:* ${finalData.status}
*Data/Hora:* ${new Date().toLocaleString("pt-BR")}`;

        const phoneNumber = import.meta.env.VITE_WHATSAPP_PHONE_NUMBER;
        const encodedMessage = encodeURIComponent(message);
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;

        window.open(whatsappUrl, "_blank");

        setTimeout(() => {
          setIsOpen(false);
          setMessages([]);
          setCurrentStep(0);
          setUserData({
            nome: "",
            telefone: "",
            email: "",
            descricao: "",
            isClient: null,
            status: "",
          });
          setShowClientOptions(false);
        }, 2000);
      }, 2000);
    }, 1500);
  };

  const handleSendMessage = async () => {
    if (!userInput.trim()) return;

    const currentQuestion = chatFlow[currentStep];
    let processedInput = userInput;

    if (currentQuestion.field === "telefone") {
      processedInput = formatPhoneNumber(userInput);
    }

    if (currentQuestion.field === "nome") {
      processedInput = processedInput
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    }

    const error = currentQuestion.validation(processedInput);
    if (error) {
      const userMessage: Message = {
        type: "user",
        text: userInput,
        timestamp: new Date(),
      };

      const errorMessage: Message = {
        type: "bot",
        text: error,
        timestamp: new Date(),
        isError: true,
      };
      setMessages((prev) => [...prev, userMessage, errorMessage]);
      setUserInput("");
      return;
    }

    const userMessage = {
      type: "user" as const,
      text: processedInput,
      timestamp: new Date(),
    };
    const newMessages: Message[] = [...messages, userMessage];

    const updatedUserData = {
      ...userData,
      [currentQuestion.field]: processedInput,
    };
    setUserData(updatedUserData);

    if (currentStep < chatFlow.length - 1) {
      setIsTyping(true);
      setMessages(newMessages);

      setTimeout(() => {
        setIsTyping(false);
        const nextQuestion = chatFlow[currentStep + 1];

        const botMessage = {
          type: "bot" as const,
          text: nextQuestion.question,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, botMessage]);
        setCurrentStep(currentStep + 1);

        if (nextQuestion.type === "options") {
          setShowClientOptions(true);
        }
      }, 1500);
    } else {
      setMessages(newMessages);
      await finalizarChat(updatedUserData);
    }

    setUserInput("");
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getCurrentQuestion = () => {
    return chatFlow[currentStep] || null;
  };

  const getPlaceholder = () => {
    const question = getCurrentQuestion();
    if (!question) return "Digite sua resposta...";

    switch (question.field) {
      case "nome":
        return "Ex: João Silva";
      case "telefone":
        return "Ex: (48) 99999-9999";
      case "email":
        return "Ex: joao@email.com";
      case "descricao":
        return "Descreva como podemos ajudar...";
      default:
        return "Digite sua resposta...";
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {isOpen && (
        <div
          ref={chatContainerRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="chat-title"
          aria-describedby="chat-description"
          className="bg-white rounded-xl shadow-2xl w-80 h-[420px] mb-4 border border-gray-200 overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white p-4 flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center" aria-hidden="true">
                <MessageCircle className="h-4 w-4" />
              </div>
              <div>
                <h3 id="chat-title" className="font-semibold text-sm">Suporte Tech Glacial</h3>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-400 rounded-full" aria-hidden="true"></div>
                  <p id="chat-description" className="text-xs opacity-90">Online agora</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              aria-label="Fechar chat"
              className="hover:bg-white/20 p-1 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-600"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          {/* Messages */}
          <div
            role="log"
            aria-live="polite"
            aria-atomic="false"
            className="flex-1 p-4 overflow-y-auto bg-gradient-to-b from-gray-50 to-gray-100"
            style={{ height: "360px" }}
          >
            {messages.map((message, index) => (
              <div
                key={index}
                className={`mb-4 ${message.type === "user" ? "text-right" : "text-left"}`}
              >
                <div
                  role={message.isError ? "alert" : undefined}
                  className={`inline-block max-w-[280px] p-3 rounded-2xl text-sm shadow-sm ${
                    message.type === "user"
                      ? "bg-blue-600 text-white rounded-br-md"
                      : message.isError
                      ? "bg-red-50 text-red-700 border-l-4 border-red-400 rounded-bl-md"
                      : "bg-white text-gray-800 border border-gray-200 rounded-bl-md"
                  }`}
                >
                  <p className="leading-relaxed">{message.text}</p>
                  <span
                    className={`block text-xs mt-2 ${
                      message.type === "user"
                        ? "text-blue-100"
                        : message.isError
                        ? "text-red-500"
                        : "text-gray-400"
                    }`}
                  >
                    {formatTime(message.timestamp)}
                  </span>
                </div>
              </div>
            ))}

            {/* Client Options */}
            {showClientOptions && (
              <div className="text-left mb-4" role="group" aria-label="Opções de resposta">
                <div className="space-y-2">
                  {chatFlow[currentStep]?.options?.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleOptionSelect(option.value)}
                      aria-label={option.label}
                      className="block w-full text-left p-3 bg-white hover:bg-blue-50 border-2 border-gray-200 hover:border-blue-300 rounded-xl transition-all duration-200 text-sm font-medium text-gray-700 hover:text-blue-700 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      <span className="flex items-center space-x-2">
                        <span
                          aria-hidden="true"
                          className={`w-3 h-3 rounded-full ${
                            option.value ? "bg-green-400" : "bg-blue-400"
                          }`}
                        ></span>
                        <span>{option.label}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Typing indicator */}
            {isTyping && (
              <div className="text-left mb-4" role="status" aria-live="polite">
                <div className="inline-block bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
                  <div className="flex space-x-1 items-center">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" aria-hidden="true"></div>
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.1s" }}
                      aria-hidden="true"
                    ></div>
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: "0.2s" }}
                      aria-hidden="true"
                    ></div>
                    <span className="text-xs text-gray-500 ml-2">Digitando...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          {!showClientOptions && (
            <div className="absolute bottom-0 left-0 right-0 z-10 p-4 border-t border-gray-200 bg-white rounded-b-xl h-[100px]">
              <form onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }} aria-label="Formulário de mensagem">
                <div className="flex space-x-3">
                  {getCurrentQuestion()?.type === "textarea" ? (
                    <textarea
                      ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={getPlaceholder()}
                      aria-label={`Responda: ${getCurrentQuestion()?.question}`}
                      className="flex-1 border border-gray-300 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                      rows={2}
                      disabled={isTyping}
                    />
                  ) : (
                    <input
                      ref={inputRef as React.RefObject<HTMLInputElement>}
                      type="text"
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={getPlaceholder()}
                      aria-label={`Responda: ${getCurrentQuestion()?.question}`}
                      className="flex-1 border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      disabled={isTyping}
                    />
                  )}
                  <button
                    type="submit"
                    onClick={handleSendMessage}
                    disabled={isTyping || !userInput.trim()}
                    aria-label="Enviar mensagem"
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white p-3 rounded-xl transition-colors shadow-lg hover:shadow-xl disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    <Send className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-xs text-gray-500" aria-live="polite">
                    Etapa {currentStep + 1} de {chatFlow.length}
                  </span>
                  <span className="text-xs text-gray-400">Pressione Enter para enviar</span>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Chat Button */}
      <button
        ref={openButtonRef}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? "Fechar chat de suporte" : "Abrir chat de suporte"}
        aria-expanded={isOpen}
        aria-controls="chat-widget"
        className="relative bg-gradient-to-r from-blue-600 to-cyan-500 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 transform group focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        <MessageCircle className="h-6 w-6" aria-hidden="true" />
        {!isOpen && (
          <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-500 rounded-full animate-pulse" aria-label="Nova mensagem disponível"></div>
        )}
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 opacity-0 group-hover:opacity-20 animate-ping" aria-hidden="true"></div>
      </button>
    </div>
  );
};

export default ChatWidget;